/**
 * @fileoverview Language Model
 * @description Defines language structure for i18n support with 30+ global languages
 * @author Liraz Amir
 * @date 2025-10-06
 */

/**
 * Language interface for internationalization
 */
export interface Language {
  /** ISO 639-1 language code (e.g., 'en', 'es', 'zh-CN') */
  code: string;

  /** English name of the language */
  name: string;

  /** Native name of the language */
  nativeName: string;

  /** Geographic region */
  region: 'Americas' | 'Europe' | 'Asia' | 'UI';

  /** Whether this is a right-to-left language */
  isRTL?: boolean;
}

/**
 * Complete list of supported languages (from PDF Issue #8)
 * Source: docs/issues/issues.pdf pages 1-2, 14-15
 */
export const SUPPORTED_LANGUAGES: Language[] = [
  // Americas
  {code: 'en', name: 'English', nativeName: 'English', region: 'Americas'},
  {code: 'es', name: 'Spanish', nativeName: 'Español', region: 'Americas'},
  {code: 'pt', name: 'Portuguese', nativeName: 'Português', region: 'Americas'},
  {code: 'fr', name: 'French', nativeName: 'Français', region: 'Americas'},
  {code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl ayisyen', region: 'Americas'},
  {code: 'nl', name: 'Dutch', nativeName: 'Nederlands', region: 'Americas'},
  {code: 'qu', name: 'Quechua', nativeName: 'Runa Simi', region: 'Americas'},
  {code: 'gn', name: 'Guaraní', nativeName: 'Avañe\'ẽ', region: 'Americas'},

  // Europe
  {code: 'de', name: 'German', nativeName: 'Deutsch', region: 'Europe'},
  {code: 'it', name: 'Italian', nativeName: 'Italiano', region: 'Europe'},
  {code: 'ru', name: 'Russian', nativeName: 'Русский', region: 'Europe'},
  {code: 'pl', name: 'Polish', nativeName: 'Polski', region: 'Europe'},
  {code: 'tr', name: 'Turkish', nativeName: 'Türkçe', region: 'Europe'},
  {code: 'sv', name: 'Swedish', nativeName: 'Svenska', region: 'Europe'},
  {code: 'ro', name: 'Romanian', nativeName: 'Română', region: 'Europe'},
  {code: 'el', name: 'Greek', nativeName: 'Ελληνικά', region: 'Europe'},
  {code: 'he', name: 'Hebrew', nativeName: 'עברית', region: 'Europe', isRTL: true},

  // Asia
  {code: 'zh-CN', name: 'Mandarin Chinese', nativeName: '中文 / Zhōngwén', region: 'Asia'},
  {code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', region: 'Asia'},
  {code: 'bn', name: 'Bengali', nativeName: 'বাংলা', region: 'Asia'},
  {code: 'ar', name: 'Arabic', nativeName: 'العربية', region: 'Asia', isRTL: true},
  {code: 'ja', name: 'Japanese', nativeName: '日本語', region: 'Asia'},
  {code: 'ko', name: 'Korean', nativeName: '한국어', region: 'Asia'},
  {code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', region: 'Asia'},
  {code: 'th', name: 'Thai', nativeName: 'ไทย', region: 'Asia'},
  {code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', region: 'Asia'},
  {code: 'ur', name: 'Urdu', nativeName: 'اردو', region: 'Asia', isRTL: true},
  {code: 'fa', name: 'Persian (Farsi)', nativeName: 'فارسی', region: 'Asia', isRTL: true},
  {code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', region: 'Asia'},

  // UI Options
  {code: 'other', name: 'Other', nativeName: 'Other', region: 'UI'},
  {code: 'prefer_not', name: 'Prefer not to say', nativeName: 'Prefer not to say', region: 'UI'},
  {code: 'auto', name: 'Auto-detect (browser)', nativeName: 'Auto-detect (browser)', region: 'UI'}
];

/**
 * Get languages by region
 */
export function getLanguagesByRegion(region: Language['region']): Language[] {
  return SUPPORTED_LANGUAGES.filter(lang => lang.region === region);
}

/**
 * Get language by code
 */
export function getLanguageByCode(code: string): Language | undefined {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
}

/**
 * Check if language is RTL
 */
export function isRTL(code: string): boolean {
  const lang = getLanguageByCode(code);
  return lang?.isRTL ?? false;
}
